import { TimeFormat } from "../types";
import { formatTimeString } from "./timeFormatting";
import { TIME_CONSTANTS } from "../constants";

/**
 * Maximum hour value for 24-hour format.
 */
export const MAX_HOUR = TIME_CONSTANTS.MAX_HOUR_24;

/**
 * Maximum minute value.
 */
export const MAX_MINUTE = TIME_CONSTANTS.MAX_MINUTE;

/**
 * Converts constraint times to normalized minutes for comparison.
 * Helper to eliminate duplicate constraint conversion logic.
 *
 * @param min - Minimum time constraint
 * @param max - Maximum time constraint
 * @returns Object with minMinutes and maxMinutes for constraint checking
 */
function getConstraintMinutes(min?: string, max?: string) {
  const minMinutes = min ? timeToMinutes(convertTimeFormat(min, 24)) : 0;
  const maxMinutes = max
    ? timeToMinutes(convertTimeFormat(max, 24))
    : TIME_CONSTANTS.MINUTES_IN_DAY;
  return { minMinutes, maxMinutes };
}

/**
 * Checks if time in minutes falls within the given constraints.
 *
 * @param timeMinutes - Time to check in minutes since midnight
 * @param minMinutes - Minimum allowed time in minutes
 * @param maxMinutes - Maximum allowed time in minutes
 * @returns True if time is within constraints
 */
function isWithinConstraints(
  timeMinutes: number,
  minMinutes: number,
  maxMinutes: number,
): boolean {
  return timeMinutes >= minMinutes && timeMinutes <= maxMinutes;
}

/**
 * Converts a time string to total minutes since midnight.
 *
 * @param timeStr - Time string in HH:mm format (24-hour)
 * @returns Total minutes since midnight
 */
export const timeToMinutes = (timeStr: string): number => {
  const match = timeStr.match(/^(\d{1,2}):(\d{2})(?:\s*(AM|PM))?$/i);
  if (!match) return 0;

  let hours = parseInt(match[1], 10);
  const minutes = parseInt(match[2], 10);
  const period = match[3]?.toUpperCase();

  // Validate minutes range
  if (minutes > MAX_MINUTE) return 0;

  if (period === "PM" && hours !== 12) {
    hours += 12;
  } else if (period === "AM" && hours === 12) {
    hours = 0;
  }

  // Validate final hours range (0-23)
  if (hours > MAX_HOUR) return 0;

  return hours * 60 + minutes;
};

/**
 * Converts a time string between 12-hour and 24-hour formats.
 *
 * @param time - Time string to convert
 * @param targetFormat - Target format (12 or 24)
 * @returns Converted time string
 */
export function convertTimeFormat(
  time: string,
  targetFormat: TimeFormat,
): string {
  if (!time) return time;

  const minutes = timeToMinutes(time);

  // If timeToMinutes returns 0, it means the input couldn't be parsed as a valid time
  // Return the original string as the JSDoc comment promises
  if (minutes === 0 && time !== "00:00" && time !== "12:00 AM") {
    return time;
  }

  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;

  return formatTimeString(hours, mins, targetFormat);
}

/**
 * Normalizes partial time input by adding missing components.
 *
 * @param value - Raw input value
 * @returns Normalized time string
 */
function normalizePartialInput(value: string): string {
  // Remove placeholder characters first (including en dashes)
  const cleanValue = value.replace(/[–_:]/g, "").replace(/\s+/g, "");

  if (/^\d{1,2}$/.test(cleanValue)) {
    // Single or double digit hours, add ":00"
    const result = `${cleanValue}:00`;
    return result;
  } else if (/^\d{1,2}:\d{1}$/.test(cleanValue)) {
    // Hours and single digit minutes, add "0"
    const result = `${cleanValue}0`;
    return result;
  } else if (/^\d{4}$/.test(cleanValue)) {
    // Four digits, insert colon after first two
    const result = `${cleanValue.slice(0, 2)}:${cleanValue.slice(2)}`;
    return result;
  } else if (/^\d{4}(AM|PM)$/i.test(cleanValue)) {
    // Four digits followed by AM/PM, insert colon after first two
    const digits = cleanValue.slice(0, 4);
    const ampm = cleanValue.slice(4);
    const result = `${digits.slice(0, 2)}:${digits.slice(2)} ${ampm}`;
    return result;
  }

  return value;
}

/**
 * Parses 12-hour format time input.
 *
 * @param normalizedValue - Normalized input value
 * @param min - Minimum allowed time constraint
 * @param max - Maximum allowed time constraint
 * @returns Parsed time components or null if invalid
 */
function parse12HourFormat(
  normalizedValue: string,
  min?: string,
  max?: string,
): { hours: number; minutes: number } | null {
  const match = normalizedValue.match(/^(\d{1,2}):(\d{1,2})(?:\s*(AM|PM))?$/i);
  if (!match) return null;

  let hours = parseInt(match[1], 10);
  let minutes = parseInt(match[2], 10);
  const ampm = match[3]?.toUpperCase();

  // Handle partial minute input (e.g., "10:5" should be "10:50")
  if (minutes < 10 && normalizedValue.match(/^(\d{1,2}):(\d{1})$/)) {
    minutes *= 10;
  }

  // Handle AM/PM conversion to 24-hour format
  if (ampm === "PM") {
    if (hours !== 12) {
      hours += 12; // Convert to 24-hour (1 PM = 13:00, 2 PM = 14:00, etc.)
    }
    // 12 PM stays as 12:00
  } else if (ampm === "AM") {
    if (hours === 12) {
      hours = 0; // 12 AM = 00:00
    }
    // All other AM times stay as-is
  } else {
    // No AM/PM specified - need to choose between AM and PM
    if (hours === 12) {
      hours = 12; // 12:00 = 12 PM
    } else {
      // For hours 1-11, check constraints to decide AM vs PM
      if (min || max) {
        // Check which interpretation (AM or PM) is valid within constraints
        const amHours = hours;
        const pmHours = hours + 12;

        const { minMinutes, maxMinutes } = getConstraintMinutes(min, max);

        const amMinutes = amHours * 60 + minutes;
        const pmMinutes = pmHours * 60 + minutes;

        const amValid = isWithinConstraints(amMinutes, minMinutes, maxMinutes);
        const pmValid = isWithinConstraints(pmMinutes, minMinutes, maxMinutes);

        if (amValid && pmValid) {
          // Both are valid, prefer AM (original behavior)
          // hours stays as-is (AM)
        } else if (pmValid) {
          // Only PM is valid, use PM
          hours = pmHours;
        } else if (amValid) {
          // Only AM is valid, use AM
          // hours stays as-is (AM)
        } else {
          // Neither is valid - this should be caught by constraint validation later
          // hours stays as-is (AM) for now
        }
      }
      // If no constraints, assume AM (original behavior)
    }
  }

  return { hours, minutes };
}

/**
 * Parses 24-hour format time input.
 *
 * @param normalizedValue - Normalized input value
 * @returns Parsed time components or null if invalid
 */
function parse24HourFormat(
  normalizedValue: string,
): { hours: number; minutes: number } | null {
  const match = normalizedValue.match(/^(\d{1,2}):(\d{1,2})$/);

  if (!match) {
    return null;
  }

  const hours = parseInt(match[1], 10);
  let minutes = parseInt(match[2], 10);

  // Handle partial minute input (e.g., "10:5" should be "10:50")
  if (minutes < 10 && normalizedValue.match(/^(\d{1,2}):(\d{1})$/)) {
    minutes *= 10;
  }

  const result = { hours, minutes };
  return result;
}

/**
 * Validates time constraints for 12-hour format with partial inputs.
 *
 * @param timeString - Formatted time string
 * @param value - Original input value
 * @param min - Minimum allowed time
 * @param max - Maximum allowed time
 * @returns True if time is within constraints
 */
function validate12HourConstraints(
  timeString: string,
  value: string,
  min?: string,
  max?: string,
): boolean {
  if (!min && !max) return true;

  // Check if the original input had AM/PM specified
  const originalHasAMPM =
    value.toUpperCase().includes("AM") || value.toUpperCase().includes("PM");

  if (!originalHasAMPM) {
    // This is a partial input without AM/PM - check if either AM or PM interpretation would be valid
    // Extract just the time part (HH:MM) without AM/PM
    const timePart = timeString.replace(/\s*(AM|PM)$/i, "");
    const amTime = `${timePart} AM`;
    const pmTime = `${timePart} PM`;

    const amMinutes = timeToMinutes(convertTimeFormat(amTime, 24));
    const pmMinutes = timeToMinutes(convertTimeFormat(pmTime, 24));

    const { minMinutes, maxMinutes } = getConstraintMinutes(min, max);

    // If either AM or PM interpretation is valid, allow the input
    return (
      isWithinConstraints(amMinutes, minMinutes, maxMinutes) ||
      isWithinConstraints(pmMinutes, minMinutes, maxMinutes)
    );
  } else {
    // AM/PM is specified, check the specific time
    const timeMinutes = timeToMinutes(convertTimeFormat(timeString, 24));
    const { minMinutes, maxMinutes } = getConstraintMinutes(min, max);

    return isWithinConstraints(timeMinutes, minMinutes, maxMinutes);
  }
}

/**
 * Validates time constraints for 24-hour format.
 *
 * @param timeString - Formatted time string
 * @param min - Minimum allowed time
 * @param max - Maximum allowed time
 * @returns True if time is within constraints
 */
function validate24HourConstraints(
  timeString: string,
  min?: string,
  max?: string,
): boolean {
  if (!min && !max) return true;

  const timeMinutes = timeToMinutes(timeString);
  const { minMinutes, maxMinutes } = getConstraintMinutes(min, max);

  // Check if the exact time is valid
  if (isWithinConstraints(timeMinutes, minMinutes, maxMinutes)) {
    return true;
  }

  // Handle partial inputs - check if this time could potentially be valid
  // For example, if someone types "1:00" but constraints are 09:00-17:00,
  // we should allow it because they might be typing "14:00"
  const hours = Math.floor(timeMinutes / 60);
  const minutes = timeMinutes % 60;

  // If this is a partial hour (like "1:00"), check if any hour in the valid range
  // could potentially match this pattern
  if (minutes === 0) {
    // Check if any hour in the valid range could start with this digit
    for (let h = minMinutes; h <= maxMinutes; h += 60) {
      const potentialHour = Math.floor(h / 60);
      if (potentialHour.toString().startsWith(hours.toString())) {
        return true;
      }
    }
  }

  // If this is a partial minute (like "14:3"), check if any minute in the valid range
  // could potentially match this pattern
  if (
    hours >= Math.floor(minMinutes / 60) &&
    hours <= Math.floor(maxMinutes / 60)
  ) {
    const hourMinutes = hours * 60;
    const hourMinMinutes = Math.max(minMinutes, hourMinutes);
    const hourMaxMinutes = Math.min(maxMinutes, hourMinutes + 59);

    for (let m = hourMinMinutes; m <= hourMaxMinutes; m++) {
      const potentialMinutes = m % 60;
      if (potentialMinutes.toString().startsWith(minutes.toString())) {
        return true;
      }
    }
  }

  return false;
}

/**
 * Parses input value and converts it to a time string with validation.
 *
 * @param value - The raw input value (may contain placeholders)
 * @param format - The time format (12 or 24 hour)
 * @param removePlaceholder - Function to remove placeholder characters
 * @param min - Minimum allowed time constraint
 * @param max - Maximum allowed time constraint
 * @returns Object containing parsed time string, validation state, and empty state
 */
export function parseInputValue(
  value: string,
  format: TimeFormat,
  removePlaceholder: (value: string) => string,
  min?: string,
  max?: string,
) {
  const valueMinusPlaceholder = removePlaceholder(value);

  if (valueMinusPlaceholder === "") {
    return {
      time: null,
      isInputValid: false,
      isInputEmpty: true,
    };
  }

  // Normalize partial inputs
  const normalizedValue = normalizePartialInput(valueMinusPlaceholder);

  // Parse based on format
  const parsed =
    format === 12
      ? parse12HourFormat(normalizedValue, min, max)
      : parse24HourFormat(normalizedValue);

  if (!parsed) {
    return {
      time: null,
      isInputValid: false,
      isInputEmpty: false,
    };
  }

  const { hours, minutes } = parsed;

  // Validate hours and minutes
  if (hours < 0 || hours > MAX_HOUR || minutes < 0 || minutes > MAX_MINUTE) {
    return {
      time: null,
      isInputValid: false,
      isInputEmpty: false,
    };
  }

  // Convert to formatted string
  const timeString = formatTimeString(hours, minutes, format);

  // Validate constraints
  const isValid =
    format === 12
      ? validate12HourConstraints(timeString, value, min, max)
      : validate24HourConstraints(timeString, min, max);

  if (!isValid) {
    return {
      time: null,
      isInputValid: false,
      isInputEmpty: false,
    };
  }

  return {
    time: timeString,
    isInputValid: true,
    isInputEmpty: false,
  };
}
